import { doc, collection, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// DESTRUCTIVE (dev project only): feedback template lifecycle. Created with
// active:false so it never shows in template pickers, createdBy = current user
// so the rules' creator-delete/creator-update path applies.
//
// Role-aware: create is asserted against the rules mirror (a correct denial
// counts as a pass); read + delete only run when create succeeded, and since a
// creator can always read/delete their own template, both are expected allowed.
export default {
  id: 'feedbackTemplateCrud',
  label: 'Feedback template create/delete',
  description: 'Feedback template lifecycle asserted against the rules for your role.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const ref = doc(collection(db, 'feedbackTemplates'))
    const expectCreate = RULE_EXPECTATIONS.feedbackTemplateCreate(role)
    let created = false

    try {
      await step(`create template (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(ref, {
            title: `SMOKE template ${ctx.runId.slice(-8)}`,
            description: 'Temporary smoke-test template — safe to delete',
            sections: [],
            active: false,
            shared: false,
            createdBy: ctx.user.uid,
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
        )
        created = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — feedbackTemplates/${ref.id}`
          : accessDetail(expectCreate, allowed)
      })

      if (!created) {
        step.skip('remaining lifecycle', 'Role cannot create — later steps skipped')
        return
      }

      await step('read template back (expected: allowed — creator)', async () => {
        const { allowed } = await attempt(async () => {
          const snap = await getDoc(ref)
          if (!snap.exists()) throw new Error('Created template not found on read-back')
        })
        return accessDetail(true, allowed)
      })

      await step('delete template (expected: allowed — creator-delete rule)', async () => {
        const { allowed } = await attempt(() => deleteDoc(ref))
        return accessDetail(true, allowed)
      })
    } finally {
      await deleteDoc(ref).catch(() => {})
    }
  }
}
